import { z, type ZodType } from '@customerio/hydra-validation';
import type { UsersSideNavPreferences, UsersUserSettingsResponse } from '@customerio/hydra-ui-api';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';
import { defineEntity } from '../shared/define-entity';

export type SideNavPreferences = UsersSideNavPreferences;

// Mirrors services_domains_users_ThemePreferences; can be swapped for the generated
// `UsersThemePreferences` type once the hydra-ui-api client is regenerated post-merge.
export interface ThemePreferences {
  color_scheme: ColorSchemePreference;
}

export interface UserSettings {
  accountId: number;
  userId: number;
  side_nav_preferences?: SideNavPreferences;
  theme_preferences?: ThemePreferences;
}

const SideNavItemPreferencesFromApi = z.looseObject({
  visibility: z.enum(['show', 'hide']),
});

const SideNavItemPreferenceMapFromApi = z.record(z.string(), SideNavItemPreferencesFromApi);

const SideNavEnvironmentPreferencesFromApi = z.looseObject({
  items: SideNavItemPreferenceMapFromApi.nullish(),
  object_types: SideNavItemPreferenceMapFromApi.nullish(),
});

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const SideNavPreferencesFromApi = z.looseObject({
  environments: z.record(z.string(), SideNavEnvironmentPreferencesFromApi).nullish(),
  items: SideNavItemPreferenceMapFromApi.nullish(),
}) as unknown as ZodType<SideNavPreferences, unknown>;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const ThemePreferencesFromApi = z.looseObject({
  color_scheme: z.enum(['auto', 'light', 'dark']),
}) as unknown as ZodType<ThemePreferences, unknown>;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const UserSettingsFromApi = z.looseObject({
  accountId: z.number(),
  userId: z.number(),
  side_nav_preferences: SideNavPreferencesFromApi.optional(),
  theme_preferences: ThemePreferencesFromApi.optional(),
}) as unknown as ZodType<UserSettings, unknown>;

export const userSettings = defineEntity<UserSettings, UserSettings, void, undefined, 'account'>({
  name: 'user-settings',
  scope: 'account',
  pk: (settings) => settings.userId,
  schema: { read: UserSettingsFromApi },
});

export const userSettingsKeys = userSettings.keys;

export function parseUserSettingsResponse(
  response: UsersUserSettingsResponse,
  context: Pick<UserSettings, 'accountId' | 'userId'>,
): UserSettings {
  return userSettings.parse({
    ...response.settings,
    accountId: context.accountId,
    userId: context.userId,
  });
}
